import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-add-invoice',
  templateUrl: './add-invoice.component.html',
  styleUrl: './add-invoice.component.css'
})
export class AddInvoiceComponent implements OnInit {
  executionFrom!: FormGroup;
  selectedDomain = 'Residential';
  isSubmitting = false;
  isLoading = false;

  // Edit mode
  isEditMode = false;
  invoiceId: number | null = null;

  // Dropdown data
  domainOptions: any[] = [];
  projectOptions: any[] = [];
  allInvoicePos: any[] = [];
  pendingPoId: number | null = null;

  optionsMap: { [key: string]: any[] } = {
    domainOptionsDrp: this.domainOptions,
    projectOptions: this.projectOptions,
    poOptions: []
  };

  domainFields: { [key: string]: any[] } = {
    Residential: [
      { key: 'unitCompletionCount', label: 'Unit Completion Count', type: 'number', required: false, value: '' },
      { key: 'interiorFinishBilling', label: 'Interior Finish Billing', type: 'currency', required: false, value: '' },
    ],
    Commercial: [
      { key: 'hvacInstallationCost', label: 'HVAC Installation Cost', type: 'currency', required: false, value: '' },
      { key: 'elevatorCommissioning', label: 'Elevator Commissioning', type: 'currency', required: false, value: '' },
    ],
    Educational: [
      { key: 'labEquipmentSupplied', label: 'Lab Equipment Supplied', type: 'text', required: false, value: '' },
      { key: 'sportsInfrastructure', label: 'Sports Infrastructure', type: 'currency', required: false, value: '' },
    ],
    Water: [
      { key: 'pipelineLaidLength', label: 'Pipeline Laid Length', type: 'text', required: false, value: '' },
      { key: 'pumpStationSetupCost', label: 'Pump Station Setup Cost', type: 'currency', required: false, value: '' },
    ],
    Power: [
      { key: 'transformerInstalled', label: 'Transformer Installed', type: 'number', required: false, value: '' },
      { key: 'cableLayingCost', label: 'Cable Laying Cost', type: 'text', required: false, value: '' },
    ],
    Gas: [
      { key: 'pipelineWeldCount', label: 'Pipeline Weld Count', type: 'number', required: false, value: '' },
      { key: 'safetyEquipmentSupplied', label: 'Safety Equipment Supplied', type: 'text', required: false, value: '' },
    ],
    Telecom: [
      { key: 'fiberCablePulledLength', label: 'Fiber Cable Pulled Length', type: 'text', required: false, value: '' },
      { key: 'signalEquipmentCost', label: 'Signal Equipment Cost', type: 'currency', required: false, value: '' },
    ],
    Road: [
      { key: 'asphaltLaidArea', label: 'Asphalt Laid Area', type: 'text', required: false, value: '' },
      { key: 'drainageSystemCost', label: 'Drainage System Cost', type: 'currency', required: false, value: '' },
    ],
    Consultancy: [
      { key: 'advisoryHoursBilled', label: 'Advisory Hours Billed', type: 'number', required: false, value: '' },
      { key: 'documentationCharges', label: 'Documentation Charges', type: 'currency', required: false, value: '' }
    ]
  };

  commonFields = [
    { key: 'projectId', label: 'Project', type: 'select', optionKey: 'projectOptions', required: true, value: '', onChange: (value: string) => this.onProjectChange(value) },
    { key: 'poId', label: 'Purchase Order', type: 'select', optionKey: 'poOptions', required: false, value: '', onChange: (value: string) => this.onPoSelected(value) },
    { key: 'vendorName', label: 'Vendor Name', type: 'text', required: true, value: '' },
    { key: 'invoiceDate', label: 'Invoice Date', type: 'date', required: true, value: new Date() },
    { key: 'invoiceAmount', label: 'Invoice Amount', type: 'currency', required: true, value: '' },
    { key: 'paymentTerms', label: 'Payment Terms', type: 'select', optionKey: 'paymentTermsOptions', required: true, value: 'Net 30' },
    { key: 'workDescription', label: 'Work Description', type: 'textarea', required: true, value: '' },
    { key: 'taxDetails', label: 'Tax Details', type: 'text', required: false, value: 'GST @ 18%' },
    { key: 'invoiceStatus', label: 'Invoice Status', type: 'select', optionKey: 'statusOptions', required: true, value: 'Submitted' },
    { key: 'paymentDueDate', label: 'Payment Due Date', type: 'date', required: false, value: '' },
    { key: 'domain', label: 'Domain', type: 'select', optionKey: 'domainOptionsDrp', required: true, value: 'Residential', onChange: (value: string) => this.onDomainChange(value) }
  ];

  pickerMap: { [key: string]: MatDatepicker<any> } = {};

  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.executionFrom = this.fb.group({});

    // Initialize domain options
    this.domainOptions = Object.keys(this.domainFields).map(key => ({
      label: key,
      value: key
    }));
    this.optionsMap['domainOptionsDrp'] = this.domainOptions;

    // Initialize payment terms options
    this.optionsMap['paymentTermsOptions'] = [
      { label: 'Net 15', value: 'Net 15' },
      { label: 'Net 30', value: 'Net 30' },
      { label: 'Net 45', value: 'Net 45' },
      { label: 'Net 60', value: 'Net 60' },
      { label: 'Due on Receipt', value: 'Due on Receipt' },
      { label: 'Advance Payment', value: 'Advance Payment' }
    ];

    // Initialize status options
    this.optionsMap['statusOptions'] = [
      { label: 'Submitted', value: 'Submitted' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Approved', value: 'Approved' },
      { label: 'Paid', value: 'Paid' },
      { label: 'Overdue', value: 'Overdue' },
      { label: 'Cancelled', value: 'Cancelled' }
    ];

    // Initialize base/common fields
    this.commonFields.forEach(field => {
      const validators = field.required ? [Validators.required] : [];
      this.executionFrom.addControl(field.key, this.fb.control(field.value || '', validators));
    });

    // Load projects for dropdown
    this.loadProjectOptions();
    this.loadPoOptions();

    // Initialize domain-specific fields
    this.onDomainChange(this.selectedDomain);

    const queryPoId = Number(this.route.snapshot.queryParamMap.get('po_id'));
    if (queryPoId) {
      this.pendingPoId = queryPoId;
    }

    // Check if we're in edit mode
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.invoiceId = +params['id'];
        this.loadInvoiceData(this.invoiceId);
      }
    });
  }

  loadProjectOptions(): void {
    this.apiService.getProjectListForDropdown({ limit: 100 }).subscribe({
      next: (response: any) => {
        this.projectOptions = (response?.data || []).map((p: any) => ({
          label: p.project_name || `Project #${p.id}`,
          value: p.id
        }));
        this.optionsMap['projectOptions'] = this.projectOptions;
      },
      error: (error) => {
        console.error('Error loading projects:', error);
        this.toastrMsgService.error('Failed to load projects');
      }
    });
  }

  loadPoOptions(): void {
    forkJoin({
      received: this.apiService.listReceivedPurchaseOrders().pipe(catchError(() => of({ data: [] }))),
      issued: this.apiService.listPurchaseOrders().pipe(catchError(() => of({ data: [] }))),
    }).subscribe({
      next: (res: any) => {
        const merged = [...(res.received?.data || []), ...(res.issued?.data || [])];
        const byId = new Map<number, any>();
        merged.forEach((po: any) => {
          if (po?.id) byId.set(Number(po.id), po);
        });
        this.allInvoicePos = Array.from(byId.values()).filter((po) =>
          ['accepted', 'amended'].includes(String(po.po_status || '').toLowerCase())
        );
        this.refreshPoDropdown();
        if (this.pendingPoId) {
          this.applyPoPrefill(this.pendingPoId);
        }
      },
    });
  }

  refreshPoDropdown(): void {
    const projectId = this.executionFrom?.get('projectId')?.value;
    const rows = this.allInvoicePos.filter((po) =>
      !projectId || Number(po.project_id) === Number(projectId)
    );
    this.optionsMap['poOptions'] = [
      { label: 'None', value: '' },
      ...rows.map((po) => ({
        label: `${po.po_ref || 'PO #' + po.id} · ${po.project_name || ''} (${po.po_status})`,
        value: po.id,
      })),
    ];
  }

  onProjectChange(_projectId: string | number): void {
    this.refreshPoDropdown();
    const currentPo = this.executionFrom.get('poId')?.value;
    if (currentPo) {
      const stillValid = this.optionsMap['poOptions']?.some((opt) => String(opt.value) === String(currentPo));
      if (!stillValid) {
        this.executionFrom.patchValue({ poId: '' });
      }
    }
  }

  onPoSelected(poId: string | number): void {
    if (!poId) return;
    this.applyPoPrefill(Number(poId));
  }

  applyPoPrefill(poId: number): void {
    const apply = (po: any) => {
      if (!po) return;
      this.pendingPoId = null;
      if (!this.allInvoicePos.some((row) => Number(row.id) === Number(po.id))) {
        this.allInvoicePos = [po, ...this.allInvoicePos];
      }
      if (po.project_id) {
        this.executionFrom.patchValue({ projectId: po.project_id });
      }
      this.refreshPoDropdown();
      this.executionFrom.patchValue({
        poId: po.id,
        vendorName: po.vendor_org_name || po.vendor_name || this.executionFrom.value.vendorName,
        invoiceAmount: po.total_value || this.executionFrom.value.invoiceAmount,
        workDescription: po.item_description || this.executionFrom.value.workDescription,
      });
    };
    const cached = this.allInvoicePos.find((po) => Number(po.id) === poId);
    if (cached) {
      apply(cached);
      return;
    }
    this.apiService.getPurchaseOrderById(poId).subscribe({
      next: (res: any) => apply(res?.data || res),
      error: () => this.toastrMsgService.error('Could not load the selected purchase order'),
    });
  }

  loadInvoiceData(id: number): void {
    this.isLoading = true;
    this.apiService.getInvoiceById(id).subscribe({
      next: (invoice: any) => {
        if (invoice) {
          // Populate form with existing invoice data
          const data = invoice.data || {};

          // Set domain first to initialize domain-specific fields
          if (data.domain) {
            this.onDomainChange(data.domain);
          }

          // Patch common fields
          this.executionFrom.patchValue({
            projectId: data.projectId || invoice.project_id,
            poId: data.poId || invoice.po_id || '',
            vendorName: data.vendorName || '',
            invoiceDate: data.invoiceDate ? new Date(data.invoiceDate) : new Date(),
            invoiceAmount: data.invoiceAmount || invoice.total,
            paymentTerms: data.paymentTerms || 'Net 30',
            workDescription: data.workDescription || '',
            taxDetails: data.taxDetails || 'GST @ 18%',
            invoiceStatus: data.invoiceStatus || 'Submitted',
            paymentDueDate: data.paymentDueDate ? new Date(data.paymentDueDate) : null,
            domain: data.domain || 'Residential'
          });

          // Patch domain-specific fields
          if (data.domain && this.domainFields[data.domain]) {
            this.domainFields[data.domain].forEach(field => {
              if (data[field.key] !== undefined) {
                this.executionFrom.patchValue({ [field.key]: data[field.key] });
              }
            });
          }
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading invoice:', err);
        this.toastrMsgService.error('Failed to load invoice data');
        this.isLoading = false;
        this.router.navigate(['/home/invoice-list']);
      }
    });
  }

  onSubmit(): void {
    if (this.executionFrom.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.executionFrom.controls).forEach(key => {
        this.executionFrom.get(key)?.markAsTouched();
      });
      this.toastrMsgService.error('Please fill all required fields');
      return;
    }

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const formValue = this.executionFrom.value;
    const project_id = formValue.projectId;
    const total = Number(formValue.invoiceAmount || 0);

    if (!project_id || !total) {
      this.toastrMsgService.error('Project and Invoice Amount are required');
      this.isSubmitting = false;
      return;
    }

    // Find project name for display
    const selectedProject = this.projectOptions.find(p => p.value === project_id);
    const projectName = selectedProject?.label || `Project #${project_id}`;

    // Build items array
    const items = [
      {
        description: formValue.workDescription || projectName,
        amount: total,
        taxDetails: formValue.taxDetails,
        domain: formValue.domain,
        metadata: {
          vendorName: formValue.vendorName,
          paymentTerms: formValue.paymentTerms
        }
      }
    ];

    // Prepare data payload with all form values
    const data = {
      ...formValue,
      projectName: projectName
    };

    if (this.isEditMode && this.invoiceId) {
      // Update existing invoice
      this.apiService.saveUpdateInvoice(this.invoiceId, { data }).subscribe({
        next: (res: any) => {
          this.toastrMsgService.success(res?.message || 'Invoice updated successfully');
          this.isSubmitting = false;
          this.router.navigate(['/home/invoice-list']);
        },
        error: (err) => {
          console.error('Error updating invoice:', err);
          this.toastrMsgService.error(
            err?.error?.message || err?.error?.errorMessage || 'Failed to update invoice'
          );
          this.isSubmitting = false;
        }
      });
    } else {
      // Create new invoice
      const payload = {
        total,
        project_id,
        po_id: formValue.poId || null,
        items,
        data
      };

      this.apiService.saveInvoice(payload).subscribe({
        next: (res: any) => {
          this.toastrMsgService.success(res?.message || 'Invoice created successfully');
          this.isSubmitting = false;
          this.router.navigate(['/home/invoice-list']);
        },
        error: (err) => {
          console.error('Error saving invoice:', err);
          this.toastrMsgService.error(
            err?.error?.message || err?.error?.errorMessage || 'Failed to save invoice'
          );
          this.isSubmitting = false;
        }
      });
    }
  }

  onReset(): void {
    if (this.isEditMode && this.invoiceId) {
      // Reload invoice data when resetting in edit mode
      this.loadInvoiceData(this.invoiceId);
    } else {
      this.executionFrom.reset();
      // Reset to default values
      this.executionFrom.patchValue({
        invoiceStatus: 'Submitted',
        paymentTerms: 'Net 30',
        taxDetails: 'GST @ 18%',
        domain: 'Residential',
        invoiceDate: new Date()
      });
      this.onDomainChange('Residential');
    }
  }

  onDomainChange(domain: string): void {
    this.selectedDomain = domain;

    // Remove old domain controls
    Object.keys(this.executionFrom.controls).forEach(key => {
      if (
        key !== 'domain' &&
        this.domainFields[domain]?.find(field => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some(field => field.key === key)
      ) {
        this.executionFrom.removeControl(key);
      }
    });

    // Add new domain controls
    this.domainFields[domain].forEach(field => {
      if (!this.executionFrom.contains(field.key)) {
        this.executionFrom.addControl(field.key, this.fb.control(field.value || ''));
      }
    });
  }
}
